// packages/shared/src/seeds/pinbar.preset.ts

import {
  APPROVE_STATUS_HUMAN,
  APPROVE_STATUS_LABELS,
  PINBAR_STATUS,
  PINBAR_STATUS_LABELS,
  PINBAR_TYPE,
  PINBAR_TYPE_LABELS,
} from "../constants/pinbar.constants";
import { AUTO_STATUS, AUTO_STATUS_LABELS } from "../constants/symbol.constants";
import {
  COLOR_CELL_GRAY,
  COLOR_HEADER_GRAY,
  COLOR_TEXT_BLACK,
  COLOR_TEXT_WHITE,
} from "../constants/table-colors";
import type { SmartTablePreset } from "../types/smart-table";

export const pinbarDefaultPreset: SmartTablePreset = {
  id: "default",
  name: "Default",
  columns: [
    "symbol",
    "symbolStatus",
    "type",
    "date",
    "pinbarStatus",
    "approveStatus",
    "strategyVersion",
    "comment",
    "reasons",
    "id",
    "timestamp.createdAt",
    "timestamp.updatedAt",
  ],
  filters: [],
  sort: [{ column: "date", direction: "desc" }],
  pageSize: 50,
  stickyColumns: ["symbol"],
  hiddenColumns: ["id", "timestamp.createdAt", "timestamp.updatedAt"],
  columnGroups: [
    {
      id: "basic",
      label: "Basic",
      columnIds: ["symbol", "symbolStatus", "type", "date"],
    },
    {
      id: "status",
      label: "Status",
      columnIds: ["pinbarStatus", "approveStatus", "strategyVersion"],
    },
    {
      id: "details",
      label: "Details",
      columnIds: ["comment", "reasons"],
      collapsedByDefault: true,
    },
    {
      id: "meta",
      label: "Meta",
      columnIds: ["id", "timestamp.createdAt", "timestamp.updatedAt"],
      collapsedByDefault: true,
    },
  ],
  columnMeta: {
    date: {
      label: { "en-US": "Date", "ru-RU": "Дата" },
      tooltip: {
        "en-US": "Candle formation date",
        "ru-RU": "Дата образования свечи",
      },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    symbol: {
      label: { "en-US": "Symbol", "ru-RU": "Тикер" },
      tooltip: { "en-US": "Symbol", "ru-RU": "Тикер" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    symbolStatus: {
      label: { "en-US": "Symbol status", "ru-RU": "Статус тикера" },
      tooltip: { "en-US": "Symbol status", "ru-RU": "Статус тикера" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    type: {
      label: { "en-US": "Type", "ru-RU": "Тип" },
      tooltip: { "en-US": "Pinbar Type", "ru-RU": "Тип пинбара" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    approveStatus: {
      label: { "en-US": "Approve Status", "ru-RU": "Статус одобрения" },
      tooltip: { "en-US": "Approve Status", "ru-RU": "Статус одобрения" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    pinbarStatus: {
      label: { "en-US": "Pinbar Status", "ru-RU": "Статус пинбара" },
      tooltip: { "en-US": "Pinbar Status", "ru-RU": "Статус пинбара" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    strategyVersion: {
      label: { "en-US": "Model", "ru-RU": "Модель" },
      tooltip: { "en-US": "Model Version", "ru-RU": "Версия модели" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
    comment: {
      label: { "en-US": "Comment", "ru-RU": "Комментарий" },
      tooltip: { "en-US": "Comment", "ru-RU": "Комментарий" },
      headerColor: COLOR_HEADER_GRAY,
      headerTextColor: COLOR_TEXT_WHITE,
      cellColor: COLOR_CELL_GRAY,
      cellTextColor: COLOR_TEXT_BLACK,
    },
  },
  availableFilters: [
    {
      propertyKey: "date",
      order: 1,
      uiType: "dateRange",
      format: {
        label: {
          "en-US": "Candle formation date",
          "ru-RU": "Дата образования свечи",
        },
        tooltip: {
          "en-US": "Candle formation date",
          "ru-RU": "Дата образования свечи",
        },
      },
      conditions: [],
    },
    {
      propertyKey: "symbol",
      order: 2,
      uiType: "like",
      format: {
        label: { "en-US": "Symbol", "ru-RU": "Тикер" },
        tooltip: { "en-US": "Symbol", "ru-RU": "Тикер" },
      },
      conditions: [],
    },
    {
      propertyKey: "symbolStatus",
      order: 3,
      uiType: "like",
      format: {
        label: { "en-US": "Symbol status", "ru-RU": "Статус тикера" },
        tooltip: { "en-US": "Symbol status", "ru-RU": "Статус тикера" },
      },
      conditions: [],
    },
    {
      propertyKey: "type",
      order: 4,
      uiType: "select",
      format: {
        label: { "en-US": "Type", "ru-RU": "Тип" },
        tooltip: { "en-US": "Pinbar Type", "ru-RU": "Тип пинбара" },
      },
      conditions: [],
    },
    {
      propertyKey: "approveStatus",
      order: 5,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Approve Status", "ru-RU": "Статус одобрения" },
        tooltip: { "en-US": "Approve Status", "ru-RU": "Статус одобрения" },
      },
      conditions: [],
    },
    {
      propertyKey: "pinbarStatus",
      order: 6,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Pinbar Status", "ru-RU": "Статус пинбара" },
        tooltip: { "en-US": "Pinbar Status", "ru-RU": "Статус пинбара" },
      },
      conditions: [],
    },
    {
      propertyKey: "strategyVersion",
      order: 7,
      uiType: "multiSelect",
      format: {
        label: { "en-US": "Model", "ru-RU": "Модель" },
        tooltip: { "en-US": "Model Version", "ru-RU": "Версия модели" },
      },
      conditions: [],
    },
  ],
  lists: [
    {
      propertyKey: "type",
      items: PINBAR_TYPE.map((value) => ({
        value,
        label: PINBAR_TYPE_LABELS[value],
      })),
    },
    {
      propertyKey: "approveStatus",
      items: APPROVE_STATUS_HUMAN.map((value) => ({
        value,
        label: APPROVE_STATUS_LABELS[value],
      })),
    },
    {
      propertyKey: "pinbarStatus",
      items: PINBAR_STATUS.map((value) => ({
        value,
        label: PINBAR_STATUS_LABELS[value],
      })),
    },
    {
      propertyKey: "symbolStatus",
      items: AUTO_STATUS.map((v) => ({
        value: v,
        label: AUTO_STATUS_LABELS[v],
      })),
    },
  ],
  version: 4,
  isSeed: true,
};
